import { useFormikContext } from 'formik'
import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import Fieldset from '../Fieldset'
import LegacyAddressField from '../../Field/Address/LegacyAddressField'
import FormikTextField from '../../../components/Formik/TextField/FormikTextField'
import FormikPhoneNumberField from '../../Formik/FormikPhoneNumberField'
import PartyForm, { PartyProps, PartyStatusDropdown } from './PartyForm'
import { DefaultButton, PrimaryButton, Stack, StackItem } from '@fluentui/react'
import { LegalPersonMutationDto, ListItemDto } from '../../../config/partyConfig'
import KvkSearchDialog, { KvkSearchDialogProps } from '../../Dialog/ChamberOfCommerce/KvkSearchDialog'
import DialogButton from '../../../components/Button/DialogButton'
import MultiUnitEntityField from '../Accounting/MultiUnitEntityField'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import FormContainer from '../../../components/Form/FormContainer'
import { EntityType } from '../Accounting/EntityField'
import * as Yup from 'yup'
import useModules from '../../../hooks/module/useModules'
import { ModuleType } from '../../../context/GlobalContext'
import useAxios from '../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import FormikField from '../../Formik/FormikField'
import AttributesList from '../Attributes/AttributesList'
import { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../../hooks/useAttributes'
import LegalStructureCombobox, { LegalStructureComboboxProps } from '../../Combobox/i18n/LegalStructureCombobox'

const getValidationSchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        name: Yup.string().label(translate('party.edit.company.name')).required(),
    })
}

function Container(props: PartyProps<LegalPersonMutationDto>): ReactElement {
    const axios = useAxios()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const { values, setFieldValue, submitForm, isSubmitting, setValues, setErrors } =
        useFormikContext<LegalPersonMutationDto>()
    const context = useFormikContext<LegalPersonMutationDto>()
    const { isViewport, getGap, viewport } = useViewport()
    const isHorizontal = useMemo<boolean>(() => isViewport('>=', Viewport.Large), [viewport])

    return (
        <FormContainer>
            <DialogButton<KvkSearchDialogProps>
                button={PrimaryButton}
                buttonProps={{ text: translate('party.edit.coc.search.title'), iconProps: { iconName: 'Search' } }}
                dialog={KvkSearchDialog}
                dialogProps={{
                    onSelectCompany: company => {
                        setValues({ ...values, ...company })
                        axios.get<ListItemDto>(`/party/search/${company.cocNumber}`).then(result => {
                            if (result?.data?.id) {
                                setErrors({ cocNumber: translate('party.edit.coc.cocNumber.error') })
                            }
                        })
                    },
                }}
            />
            <Fieldset legend={translate('party.edit.company.label')}>
                <FormikTextField
                    context={context}
                    label={translate('party.edit.company.name')}
                    fieldName="name"
                    textFieldProps={{
                        required: true,
                        multiline: true,
                        rows: 2,
                        autoFocus: true,
                        autoAdjustHeight: true,
                    }}
                />
                <Stack horizontal={isHorizontal} tokens={{ childrenGap: getGap() }}>
                    <StackItem grow>
                        <FormikField<LegalStructureComboboxProps>
                            labelKey={'party.edit.company.legalstructure'}
                            context={context}
                            fieldName={'legalStructure'}
                            component={LegalStructureCombobox}
                            componentProps={{
                                selectedOptions: [context.values.legalStructure],
                                onOptionSelect: (_, data) => setFieldValue('legalStructure', data.selectedOptions?.[0]),
                                clearable: false,
                            }}
                        />
                    </StackItem>
                    <StackItem grow>
                        <FormikTextField
                            context={context}
                            label={translate('party.edit.company.cocNumber')}
                            fieldName="cocNumber"
                        />
                    </StackItem>
                </Stack>
            </Fieldset>

            <Fieldset legend={translate('party.edit.contact.label')} canToggle open>
                <FormikPhoneNumberField
                    label={translate('party.edit.contact.phonelandline')}
                    fieldName="phoneLandline"
                />
                <FormikTextField context={context} label={translate('party.edit.contact.email')} fieldName="email" />
            </Fieldset>

            <LegacyAddressField legend={translate('party.edit.address.label')} prefix="address" open />
            <LegacyAddressField legend={translate('party.edit.poboxaddress.label')} prefix="poBoxAddress" />

            {modules.has(ModuleType.Attributes) && (
                <Fieldset legend={translate('party.edit.attributes.label')} canToggle>
                    <FormContainer>
                        <AttributesList
                            entityType={EntityAttributeType.Party}
                            context={[AttributeContextType.LegalPerson]}
                            viewType={AttributeViewType.Input}
                            {...props}
                            orientation={isHorizontal ? 'horizontal' : 'vertical'}
                            entity={values}
                            entityId={values.id}
                            setFieldValue={setFieldValue}
                        />
                    </FormContainer>
                </Fieldset>
            )}

            {modules.has(ModuleType.Invoices) && (
                <Fieldset legend={translate('party.edit.finance.label')} canToggle>
                    <Stack horizontal tokens={{ childrenGap: getGap() }}>
                        <StackItem grow styles={{ root: { overflow: 'hidden' } }}>
                            <FormikTextField
                                context={context}
                                label={translate('party.edit.finance.financeterminvoice')}
                                fieldName="financeTermInvoice"
                                textFieldProps={{
                                    inputClassName: 'ms-textAlignRight',
                                    suffix: translate('party.edit.finance.financetermpayment.days'),
                                }}
                            />
                        </StackItem>
                        <StackItem grow styles={{ root: { overflow: 'hidden' } }}>
                            <FormikTextField
                                context={context}
                                label={translate('party.edit.finance.financetermpayment')}
                                fieldName="financeTermPayment"
                                textFieldProps={{
                                    inputClassName: 'ms-textAlignRight',
                                    suffix: translate('party.edit.finance.financetermpayment.days'),
                                }}
                            />
                        </StackItem>
                    </Stack>
                    <FormikTextField
                        context={context}
                        label={translate('party.edit.finance.financeemail')}
                        fieldName="financeEmail"
                    />
                    <FormikTextField
                        context={context}
                        label={translate('party.edit.finance.vatnumber')}
                        fieldName="vatNumber"
                    />
                    {modules.has(ModuleType.Accounting) && (
                        <MultiUnitEntityField fieldName="externals" externalType={EntityType.Customer} />
                    )}
                </Fieldset>
            )}
            <Fieldset legend={translate('party.edit.other.label')} canToggle>
                <PartyStatusDropdown />
            </Fieldset>
            <Stack horizontal tokens={{ childrenGap: getGap() }}>
                <StackItem>
                    <DefaultButton
                        primary={true}
                        text={translate('action.save')}
                        disabled={isSubmitting}
                        onClick={submitForm}
                    />
                </StackItem>
                <StackItem>
                    <DefaultButton primary={false} text={translate('action.cancel')} onClick={props.goBack} />
                </StackItem>
            </Stack>
        </FormContainer>
    )
}

export default function LegalPersonForm(props: PartyProps<LegalPersonMutationDto>): ReactElement {
    const { t: translate } = useTranslation()

    return (
        <PartyForm<LegalPersonMutationDto>
            {...props}
            formikProps={{ ...props.formikProps, validationSchema: getValidationSchema(translate) }}
        >
            <Container {...props} />
        </PartyForm>
    )
}
